import { useCallback, useEffect, useState } from "react";
import { FileDown, Loader2, Clock, GitCompare } from "lucide-react";
import { http, downloadBlob } from "../../lib/api";
import { Btn, Card, Pill } from "../kit/Kit";
import { Compare } from "./Compare";

export const VERSION_STATUS = {
  en_revision: ["En revisión", "bg-amber-50 text-amber-700 border-amber-200"],
  aprobado: ["Aprobado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  devuelto: ["Devuelto", "bg-orange-50 text-orange-700 border-orange-200"],
  rechazado: ["Rechazado", "bg-rose-50 text-rose-700 border-rose-200"],
};
const fdt = (s) => (s ? new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—");

export const useVersions = (id, dep) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => http.get(`/expedientes/${id}/versions`).then((r) => setRows(r.data)), [id]);
  useEffect(() => { load(); }, [load, dep]);
  useEffect(() => {
    if (!rows?.some((v) => v.pdf_status !== "done" && v.pdf_status !== "error")) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [rows, load]);
  return rows;
};

export const VersionPdf = ({ id, v, nombre, kind = "secondary", label }) => {
  const ready = v.pdf_status === "done";
  return (
    <Btn kind={kind} className="px-3.5 py-2" disabled={!ready} data-testid={`version-pdf-v${v.version}`}
      onClick={() => downloadBlob(`/expedientes/${id}/versions/${v.id}/pdf`, `Credit_Memo_${nombre.slice(0, 30)}_v${v.version}.pdf`)}>
      {ready ? <FileDown size={15} /> : <Loader2 size={15} className="animate-spin" />} {ready ? (label || `PDF v${v.version}`) : "Generando PDF…"}
    </Btn>
  );
};

const ACT_COLOR = { aprobar: "bg-emerald-500", devolver: "bg-orange-500", rechazar: "bg-rose-500", "envió": "bg-[#111]", dictamen: "bg-[#E8643C]", correo: "bg-sky-500", correo_error: "bg-rose-400" };

export const Historial = ({ id, exp }) => {
  const versions = useVersions(id, exp.status + exp.version);
  const [acts, setActs] = useState([]);
  const [cmp, setCmp] = useState(false);
  useEffect(() => { http.get(`/expedientes/${id}/activity`).then((r) => setActs(r.data)); }, [id, exp.status, exp.version]);
  if (cmp && versions?.length >= 2) {
    return (<div className="space-y-4"><Btn kind="ghost" onClick={() => setCmp(false)} data-testid="compare-close">← Volver al historial</Btn><Compare id={id} versions={versions} /></div>);
  }
  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <Card title="Versiones" subtitle="Cada envío a revisión crea una versión con su PDF." className="lg:col-span-7" testid="historial-versiones"
        right={versions?.length >= 2 && <Btn kind="accent" className="px-3.5 py-2" onClick={() => setCmp(true)} data-testid="compare-open"><GitCompare size={15} /> Comparar versiones</Btn>}>
        {versions?.length === 0 && <p className="text-sm text-[#8C867A]" data-testid="historial-sin-versiones">Aún no hay versiones enviadas a revisión.</p>}
        <div className="space-y-4">
          {versions?.map((v) => (
            <div key={v.id} className="rounded-2xl border border-[#E2DDD5] p-5" data-testid={`version-row-v${v.version}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="font-serif-display text-2xl font-bold">v{v.version}</span>
                  <Pill cls={VERSION_STATUS[v.status][1]} testid={`version-status-v${v.version}`}>{VERSION_STATUS[v.status][0]}</Pill>
                </div>
                <VersionPdf id={id} v={v} nombre={exp.nombre} />
              </div>
              <p className="mt-2 text-sm text-[#5C5850]">Enviada por <b>{v.submitted_by?.name}</b> · {fdt(v.submitted_at)}</p>
              {v.review && (
                <div className="mt-3 rounded-xl bg-[#FAF8F5] px-4 py-3 text-sm" data-testid={`version-review-v${v.version}`}>
                  <p className="font-semibold">{v.review.by?.name} · {fdt(v.review.at)}</p>
                  {v.review.comentario ? <p className="mt-1 text-[#5C5850]">“{v.review.comentario}”</p> : <p className="mt-1 text-[#8C867A]">Sin comentario</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
      <Card title="Bitácora de actividad" className="lg:col-span-5" testid="historial-bitacora">
        <ol className="relative space-y-5 border-l border-[#E2DDD5] pl-6">
          {acts.map((a) => (
            <li key={a.id} className="relative" data-testid="activity-item">
              <span className={`absolute -left-[29px] top-1.5 h-2.5 w-2.5 rounded-full ring-4 ring-white ${ACT_COLOR[a.accion] || "bg-[#C9C3B8]"}`} />
              <p className="text-sm"><b>{a.user_name}</b> <span className="text-[#5C5850]">{a.detalle}</span></p>
              <p className="mt-0.5 flex items-center gap-1 text-xs text-[#8C867A]"><Clock size={11} /> {fdt(a.at)}</p>
            </li>
          ))}
          {acts.length === 0 && <p className="text-sm text-[#8C867A]">Sin actividad.</p>}
        </ol>
      </Card>
    </div>
  );
};
